<script setup lang="ts">
import { GpAvatar, GpBrandLoading, GpBtn, GpCard } from '@gportal/uikit';
import ConfirmProjectInviteDeclineModal from '@panel/components/project/invite/ConfirmProjectInviteDeclineModal.vue';
import useProjectInviteAccept from '@panel/components/project/invite/composables/useProjectInviteAccept';
import useProjectInviteDecline from '@panel/components/project/invite/composables/useProjectInviteDecline';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import type { BasicProject } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { computed, ref, watchEffect } from 'vue';
import ContinueButton from './shared/ContinueButton.vue';
import WelcomeSection from './shared/WelcomeSection.vue';
import WelcomeSectionBody from './shared/WelcomeSectionBody.vue';
import { faSync } from '@fortawesome/pro-solid-svg-icons';
import useSetupValidation from './composables/useSetupValidation';

const isValid = defineModel<boolean>('isValid', {
  default: false,
});

const emit = defineEmits<{
  /**
   * Go to node limit section
   */
  skipButtonClicked: [];
  /**
   * Go to project create
   */
  nextButtonClicked: [];
}>();

const projectStore = useProjectStore();
const { userHasProjects } = useSetupValidation();

const userHasProjectInvites = computed(() => projectStore.invites.length > 0);

const { isExecutingInviteAccept, executeProjectInviteAccept } = useProjectInviteAccept();

const {
  inviteDeclineProject,
  inviteDeclineModalOpened,
  declineProject,
  executeProjectInviteDecline,
  isExecutingInviteDecline,
  abortInviteDecline,
} = useProjectInviteDecline();

const processInvite = async (project: BasicProject, accept: boolean) => {
  if (accept) {
    await executeProjectInviteAccept(project);
  } else {
    declineProject(project);
  }
};

const isProcessingInvite = computed(
  () => isExecutingInviteAccept.value || isExecutingInviteDecline.value,
);

const { addNotification } = useNotifications();
const isRefreshingInvites = ref(false);
const refreshInvites = async () => {
  isRefreshingInvites.value = true;
  try {
    await projectStore.reloadProjects();
  } catch (error) {
    addNotification({
      type: 'danger',
      message: (error as ServiceError).message,
    });
  } finally {
    isRefreshingInvites.value = false;
  }
};

watchEffect(() => {
  if (userHasProjects.value) {
    isValid.value = true;
  }
});
</script>

<template>
  <WelcomeSection
    name="Project Invite"
    :is-done="userHasProjects"
    v-playwright="'welcome-project-invites-section'"
  >
    <WelcomeSectionBody v-if="userHasProjects">
      <template #default>
        <p>You already joined the following projects:</p>

        <GpCard class="tw-mt-4" :enlarge-on-hover="false" :border-on-hover="false">
          <template #content>
            <div class="tw-flex tw-items-center tw-gap-4 tw-p-4">
              <GpAvatar
                :src="projectStore.project.avatarUrl"
                :alt="projectStore.project.name"
                class="tw-h-20 tw-w-auto"
                custom-size
              />
              <div
                class="tw-flex-1"
                v-playwright="'welcome-project-invites-section-joined-project-name'"
              >
                <p class="tw-text-lg tw-font-bold">{{ projectStore.project.name }}</p>
              </div>
            </div>
          </template>
        </GpCard>
      </template>

      <template #footer>
        <p class="tw-text-center">You can continue to the next step.</p>

        <ContinueButton
          class="tw-mx-auto tw-mt-4"
          @click="emit('skipButtonClicked')"
          v-playwright="'welcome-project-invites-section-joined-project-continue'"
        />
      </template>
    </WelcomeSectionBody>

    <WelcomeSectionBody v-else-if="userHasProjectInvites">
      <template #default>
        <div class="tw-flex-1">
          <GpBrandLoading v-if="isRefreshingInvites" class="tw-mt-8" label="Loading invites" />
          <template v-else>
            <p>
              You have been invited to {{ projectStore.invites.length }}
              {{ projectStore.invites.length === 1 ? 'project' : 'projects' }}.
            </p>
            <GpCard
              v-for="(invite, index) in projectStore.invites"
              :enlarge-on-hover="false"
              :border-on-hover="false"
              class="tw-mt-4"
              :key="invite.id"
              v-playwright="`welcome-project-invites-section-invite-${index}`"
            >
              <template #content>
                <div class="tw-flex tw-gap-4 tw-p-4">
                  <GpAvatar :src="invite.project.avatarUrl" :alt="invite.project.name" size="lg" />
                  <div class="tw-flex-1">
                    <p
                      class="tw-text-lg tw-font-bold"
                      v-playwright="`welcome-project-invites-section-invite-${index}-name`"
                    >
                      {{ invite.project.name }}
                    </p>
                    <div class="tw-mt-4 tw-flex tw-justify-end tw-gap-4">
                      <GpBtn
                        type="button"
                        btn-type="success"
                        label="Accept"
                        size="sm"
                        :disabled="isProcessingInvite"
                        @click="processInvite(invite.project, true)"
                      />
                      <GpBtn
                        type="button"
                        btn-type="danger"
                        label="Decline"
                        size="sm"
                        :disabled="isProcessingInvite"
                        @click="processInvite(invite.project, false)"
                      />
                    </div>
                  </div>
                </div>
              </template>
            </GpCard>
          </template>
          <p class="tw-mt-4">Not seeing the invite you are looking for?</p>
          <GpBtn
            class="tw-mx-auto tw-mt-4 tw-block"
            label="Refresh Invites"
            :icon="faSync"
            :disabled="isRefreshingInvites"
            @click="refreshInvites"
          />
        </div>
        <ConfirmProjectInviteDeclineModal
          :is-opened="inviteDeclineModalOpened"
          :project-name="inviteDeclineProject == null ? '' : inviteDeclineProject.name"
          @decline="executeProjectInviteDecline"
          @confirm="abortInviteDecline"
        />
      </template>

      <template #footer>
        <p class="tw-text-center">Alternatively you can create a new project.</p>
        <ContinueButton class="tw-mx-auto tw-mt-4" @click="emit('nextButtonClicked')" />
      </template>
    </WelcomeSectionBody>

    <WelcomeSectionBody v-else>
      <template #default>
        <GpBrandLoading v-if="isRefreshingInvites" class="tw-mt-8" label="Loading invites" />
        <p v-else v-playwright="'welcome-project-invites-section-no-invites'">
          You have no project invites. Either ask a project owner to invite you
        </p>

        <GpBtn
          class="tw-mx-auto tw-mt-4 tw-block"
          label="Refresh Invites"
          :icon="faSync"
          :disabled="isRefreshingInvites"
          @click="refreshInvites"
        />
      </template>

      <template #footer>
        <p class="tw-text-center">or create a new project.</p>
        <GpBtn
          class="tw-mx-auto tw-mt-4 tw-block tw-w-1/2"
          label="Create a project"
          :disabled="isRefreshingInvites"
          v-playwright="'welcome-project-invites-section-create-project'"
          @click="emit('nextButtonClicked')"
        />
      </template>
    </WelcomeSectionBody>
  </WelcomeSection>
</template>

<style scoped></style>
